import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../AuthContext';
import { db } from '../firebase';
import {
  collection,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import { useLocation, useNavigate } from 'react-router-dom';
import { buildStudentProgressSummary } from '../utils/progressTracking';
import { buildProgressionExperience } from '../utils/progressionExperience';
import { buildSkillDetailViewModel } from '../utils/skillDetail';
import { buildSkillRoadmapModel } from '../utils/skillRoadmap';
import { buildMySkillsPath } from '../utils/mySkillsPath';
import { buildStudentLessonPath } from '../utils/studentLessonPath';
import { normalizeSportName } from '../utils/drillTemplates';
import { loadVisibleLessonsForStudent } from '../utils/studentLessonAccess';
import { scopeToOrganization } from '../utils/studentDataAccess';
import { useStudentContext } from '../hooks/useStudentContext';
import StudentOrgBar from '../components/student/StudentOrgBar';

const SPORT_OPTIONS = [
  { key: 'skiing', label: 'Skiing' },
  { key: 'snowboarding', label: 'Snowboarding' },
];

function getSportTheme(sport) {
  if (sport === 'snowboarding') {
    return {
      dot: 'bg-emerald-500',
      active: 'border-emerald-500 bg-emerald-50 text-emerald-700 shadow-sm',
      focus: 'focus:ring-emerald-300',
      badge: 'bg-emerald-100 text-emerald-700',
    };
  }

  return {
    dot: 'bg-indigo-500',
    active: 'border-indigo-500 bg-indigo-50 text-indigo-700 shadow-sm',
    focus: 'focus:ring-indigo-300',
    badge: 'bg-indigo-100 text-indigo-700',
  };
}

// Pass 78 — truthful status vocabulary (see skillRoadmap.js). No 'completed' fabricated from curriculum ordering.
function getRoadmapStatusClasses(status) {
  switch (status) {
    case 'current_focus':
      return 'border-indigo-300 bg-indigo-50 text-indigo-900';
    case 'suggested_next':
      return 'border-emerald-300 bg-emerald-50 text-emerald-900';
    case 'demonstrated':
      return 'border-teal-300 bg-teal-50 text-teal-900';
    case 'locked':
      return 'border-slate-200 bg-slate-50 text-slate-400';
    default:
      return 'border-slate-200 bg-white text-slate-700';
  }
}

function getStatusLabel(status) {
  switch (status) {
    case 'current_focus':
      return 'Current focus';
    case 'suggested_next':
      return 'Suggested next';
    case 'demonstrated':
      return 'Demonstrated';
    case 'locked':
      return 'Locked';
    default:
      return 'Available';
  }
}

function getNodeClasses(status) {
  switch (status) {
    case 'current_focus':
      return 'border-indigo-500 bg-indigo-500';
    case 'suggested_next':
      return 'border-emerald-500 bg-emerald-500';
    case 'demonstrated':
      return 'border-teal-500 bg-teal-500';
    case 'locked':
      return 'border-slate-200 bg-white';
    default:
      return 'border-slate-300 bg-white';
  }
}

function getDominantSport(videos = []) {
  const counts = new Map();
  (videos || []).forEach((video) => {
    const sport = normalizeSportName(video?.sport || '');
    if (!sport) return;
    counts.set(sport, (counts.get(sport) || 0) + 1);
  });

  return Array.from(counts.entries()).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))[0]?.[0] || 'skiing';
}

function MySkillsPage() {
  const { currentUser, userData } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [videos, setVideos] = useState([]);
  const [feedbackMap, setFeedbackMap] = useState({});
  const [lessonInstances, setLessonInstances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);
  const [selectedSport, setSelectedSport] = useState('');
  const [selectedSkillId, setSelectedSkillId] = useState('');
  const [highlightedSkillId, setHighlightedSkillId] = useState('');
  // Pass 85.1A.1 (§20/§25) — MODERN progress is scoped to the selected participant + org and uses ONLY safe modern
  // evidence: participant videos (org+participantId) + their feedback — NEVER the legacy email lesson claim, the full
  // lessonInstance, clientNotes, a tenantless video, or shared group stage status as mastery. If the progression engine
  // cannot consume the safe lesson projection yet, modern lessons are omitted (an honest reduced state, not fabricated).
  // A `dataScope` stamp on the loaded data lets the render SUPPRESS a stale scope synchronously (A→B) before reload.
  const ctx = useStudentContext(currentUser?.uid || '');
  const lane = ctx.lane;
  const isModern = lane === 'modern';
  const isLegacy = lane === 'legacy';
  const modern = isModern; // (progression render below keys off `modern`)
  const [dataScope, setDataScope] = useState('');
  // Pass 85.1A.2 — the requested data scope comes from the CANONICAL LANE, never from `!modern`. A legacy scope begins
  // ONLY when lane === 'legacy'; a modern scope ONLY when lane === 'modern'; every unresolved canonical state
  // (loading / error / needs_org / ambiguous) requests '' → NO legacy fetch, NO modern query, an honest state instead.
  const requestedScope = isModern
    ? `m::${currentUser?.uid || ''}::${ctx.selectedOrganizationId}::${ctx.activeParticipantId}`
    : isLegacy
      ? `l::${currentUser?.uid || ''}::${userData?.orgId || ''}`
      : '';
  const skillNodeRefs = useRef({});
  const preferredSport = useMemo(
    () => normalizeSportName(new URLSearchParams(location.search).get('sport') || ''),
    [location.search]
  );
  const preferredSkillId = useMemo(
    () => String(new URLSearchParams(location.search).get('skill') || '').trim(),
    [location.search]
  );

  useEffect(() => {
    // Fetch ONLY in a resolved data lane (requestedScope is '' for loading/error/needs_org/ambiguous → no fetch at all).
    if (!requestedScope || !currentUser?.uid) return undefined;
    const scopeAtStart = requestedScope;
    let alive = true;
    const fetchVideosAndFeedback = async () => {
      setLoading(true);
      setError('');
      try {
        let sortedVideos = [];
        let visibleLessons = [];
        if (isModern) {
          // MODERN: participant videos (org+participantId) only; NO email lesson claim, NO full lessonInstance.
          const snap = await getDocs(query(collection(db, 'videos'), where('organizationId', '==', ctx.selectedOrganizationId), where('participantId', '==', ctx.activeParticipantId)));
          sortedVideos = snap.docs.map((d) => ({ id: d.id, ...d.data() })).sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
          // lessons omitted from the progression engine for now (honest reduced modern state — no full lessonInstance).
        } else {
          // LEGACY compatibility: uid videos + in-memory org filter + the legacy visible-lessons path.
          const snapshot = await getDocs(query(collection(db, 'videos'), where('userId', '==', currentUser.uid)));
          const { lessons } = await loadVisibleLessonsForStudent(db, currentUser, { studentName: userData?.name || '' });
          visibleLessons = lessons;
          sortedVideos = scopeToOrganization(snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })), userData?.orgId || '')
            .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
        }

        const fbMap = {};
        await Promise.all(
          sortedVideos.map(async (video) => {
            if (video.status === 'reviewed') {
              const fbSnap = await getDocs(collection(db, 'videos', video.id, 'timestampedFeedback'));
              fbMap[video.id] = (fbSnap?.docs || []).map((feedbackDoc) => feedbackDoc.data()).sort((a, b) => a.timestamp - b.timestamp);
            }
          })
        );
        if (!alive || scopeAtStart !== requestedScope) return; // a scope switch happened mid-flight → drop this result
        setVideos(sortedVideos);
        setLessonInstances(visibleLessons);
        setFeedbackMap(fbMap);
        setDataScope(scopeAtStart);
        setSelectedSport((current) => current || preferredSport || getDominantSport(sortedVideos));
      } catch (err) {
        // Pass 78 — an actual load error is DISTINCT from "no evidence"; never render a failed query as "Not established".
        console.error('Error loading My Skills page:', err);
        if (alive) setError('We could not load your progress right now.');
      } finally {
        if (alive) setLoading(false);
      }
    };

    fetchVideosAndFeedback();
    return () => { alive = false; };
  }, [lane, isModern, ctx.selectedOrganizationId, ctx.activeParticipantId, requestedScope, currentUser, preferredSport, userData?.name, userData?.orgId, reloadKey]);

  const availableSports = useMemo(
    () =>
      Array.from(
        new Set(
          videos
            .map((video) => normalizeSportName(video?.sport || ''))
            .filter((sport) => sport === 'skiing' || sport === 'snowboarding')
        )
      ),
    [videos]
  );

  useEffect(() => {
    if (!selectedSport) {
      setSelectedSport(preferredSport || getDominantSport(videos) || 'skiing');
    }
  }, [preferredSport, selectedSport, videos]);

  useEffect(() => {
    if (preferredSport && preferredSport !== selectedSport) {
      setSelectedSport(preferredSport);
    }
  }, [preferredSport, selectedSport]);

  // Pass 85.1A.1 — SYNCHRONOUS scope suppression: expose data ONLY when the loaded scope matches the requested one, so
  // an org/participant A→B switch shows empty + loading at THIS render, before the reload effect runs (no stale flash).
  const scopeReady = !!requestedScope && dataScope === requestedScope;
  const effectiveVideos = useMemo(() => (scopeReady ? videos : []), [scopeReady, videos]);
  const effectiveLessons = scopeReady ? lessonInstances : [];
  const effectiveFeedback = scopeReady ? feedbackMap : {};
  // A canonical read failure is surfaced as an ERROR, never as "no evidence"; needs_org/ambiguous show their own state
  // (via StudentOrgBar) rather than a spurious spinner; loading + a still-unresolved data lane show loading.
  const effectiveError = error || lane === 'error';
  const unresolvedCanonical = lane === 'needs_org' || lane === 'ambiguous';
  const effectiveLoading = !effectiveError && !unresolvedCanonical && (loading || lane === 'loading' || !scopeReady);
  const sportVideos = useMemo(
    () => effectiveVideos.filter((video) => normalizeSportName(video?.sport || '') === selectedSport),
    [effectiveVideos, selectedSport]
  );
  // Pass 77 — the selected sport is EXPLICIT context: it flows through the whole progression contract so review
  // AND lesson evidence are both filtered to this sport (no dominant-sport guessing; lesson-only sport supported).
  const summary = buildStudentProgressSummary(sportVideos, { feedbackMap: effectiveFeedback, lessonInstances: effectiveLessons, sport: selectedSport });
  // Pass 78 — the truthful, evidence-led experience view-model: focus / demonstrated level / next step / why + evidence.
  const experience = buildProgressionExperience(summary);
  const roadmap = buildSkillRoadmapModel(selectedSport, summary);
  const unlockedBranches = roadmap.branchTracks.filter((track) => track.isUnlocked);
  const skillDetail = buildSkillDetailViewModel({
    sport: selectedSport,
    selectedSkillId,
    roadmap,
  });

  useEffect(() => {
    const skillIds = roadmap.tracks.flatMap((track) => track.skills.map((skill) => skill.skillId));
    if (skillIds.length === 0) {
      setSelectedSkillId('');
      return;
    }

    if (selectedSkillId && skillIds.includes(selectedSkillId)) {
      return;
    }

    setSelectedSkillId(
      (preferredSkillId && skillIds.includes(preferredSkillId) && preferredSkillId) ||
      roadmap.currentSkillId ||
      roadmap.nextSkillId ||
      roadmap.coreTrack?.skills[0]?.skillId ||
      skillIds[0]
    );
  }, [preferredSkillId, roadmap, selectedSkillId]);

  useEffect(() => {
    if (!selectedSport) return;
    const nextPath = buildMySkillsPath({
      sport: selectedSport,
      skill: selectedSkillId || '',
    });
    const currentPath = `${location.pathname}${location.search}`;
    if (currentPath !== nextPath) {
      navigate(nextPath, { replace: true });
    }
  }, [location.pathname, location.search, navigate, selectedSkillId, selectedSport]);

  useEffect(() => {
    if (!selectedSkillId) return;
    setHighlightedSkillId(selectedSkillId);
    const timeout = window.setTimeout(() => {
      setHighlightedSkillId((current) => (current === selectedSkillId ? '' : current));
    }, 1200);
    return () => window.clearTimeout(timeout);
  }, [selectedSkillId]);

  useEffect(() => {
    if (!selectedSkillId) return;
    const node = skillNodeRefs.current[selectedSkillId];
    if (node?.scrollIntoView) {
      node.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [selectedSkillId]);

  return (
    <div className="max-w-6xl mx-auto p-4" data-student-mode={modern ? 'modern' : 'legacy'}>
      <StudentOrgBar ctx={ctx} />
      <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">My Progress</p>
        <h1 className="mt-2 text-2xl font-semibold text-slate-950">My Skills</h1>
        <p className="mt-2 max-w-3xl text-sm text-slate-600">
          What you&rsquo;re working on, what you&rsquo;ve demonstrated, and your next step — built only from real coach reviews and completed lessons. Explore the full skill roadmap below.
        </p>

        <div className="mt-5 flex flex-wrap gap-3">
          {SPORT_OPTIONS.map((sport) => {
            const isActive = selectedSport === sport.key;
            const hasActivity = availableSports.includes(sport.key);
            const theme = getSportTheme(sport.key);
            return (
              <button
                key={sport.key}
                type="button"
                onClick={() => setSelectedSport(sport.key)}
                aria-pressed={isActive}
                className={`rounded-xl border px-4 py-2 text-sm font-medium ${
                  isActive
                    ? theme.active
                    : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                } ${!hasActivity ? 'opacity-90' : ''} focus:outline-none focus:ring-2 ${theme.focus}`}
              >
                <span className="flex items-center gap-2">
                  <span className={`h-2.5 w-2.5 rounded-full ${theme.dot}`} />
                  <span>{sport.label}</span>
                  {isActive && (
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${theme.badge}`}>
                      Selected
                    </span>
                  )}
                </span>
              </button>
            );
          })}
        </div>

        {effectiveLoading ? (
          <p className="mt-6 text-sm text-slate-500" role="status">Loading your progress…</p>
        ) : effectiveError ? (
          <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-4" role="alert">
            <p className="text-sm text-amber-900">{error || 'We could not load your coaching right now.'}</p>
            <button
              type="button"
              onClick={() => setReloadKey((k) => k + 1)}
              className="mt-3 rounded-xl border border-amber-300 bg-white px-4 py-2 text-sm font-medium text-amber-800 hover:bg-amber-100"
            >
              Try again
            </button>
          </div>
        ) : (
          <>
            {experience.hasEvidence ? (
              <>
                {/* TRUTHFUL HIERARCHY — focus / demonstrated level / next step (mobile-first: stacks, then 3-up) */}
                <div className="mt-6 grid gap-4 sm:grid-cols-3">
                  <div className="rounded-2xl border border-indigo-200 bg-indigo-50 px-4 py-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-indigo-500">Your focus</p>
                    <p className="mt-2 text-base font-semibold text-indigo-950">{experience.focus.value}</p>
                    <p className="mt-1 text-xs text-indigo-700">What your coaching evidence says to work on now</p>
                  </div>
                  <div className="rounded-2xl border border-teal-200 bg-teal-50 px-4 py-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-teal-600">Demonstrated level</p>
                    <p className="mt-2 text-base font-semibold text-teal-950">{experience.demonstratedLevel.value}</p>
                    <p className="mt-1 text-xs text-teal-700">Only from a lesson outcome that demonstrated a skill</p>
                  </div>
                  <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-emerald-600">Next step</p>
                    <p className="mt-2 text-base font-semibold text-emerald-950">{experience.nextStep.value}</p>
                    <p className="mt-1 text-xs text-emerald-700">The recommended skill to build toward next</p>
                  </div>
                </div>

                {/* WHY THIS FOCUS — plain-language reason + links back to the canonical review/lesson evidence */}
                {experience.why.hasReason || experience.why.evidence.length > 0 ? (
                  <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Why this focus</p>
                    {experience.why.hasReason ? (
                      <p className="mt-2 text-sm text-slate-800">{experience.why.text}</p>
                    ) : null}
                    {experience.why.evidence.length > 0 ? (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {experience.why.evidence.map((item) =>
                          item.hasRoute ? (
                            <button
                              key={item.key}
                              type="button"
                              onClick={() => navigate(item.route)}
                              className="rounded-full border border-slate-300 bg-white px-3 py-1 text-xs font-medium text-slate-700 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-slate-300"
                            >
                              {item.kind}{item.dateText ? ` · ${item.dateText}` : ''} → {item.label}
                            </button>
                          ) : (
                            <span
                              key={item.key}
                              className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs text-slate-600"
                            >
                              {item.kind}{item.dateText ? ` · ${item.dateText}` : ''}{item.label ? ` · ${item.label}` : ''}
                            </span>
                          )
                        )}
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </>
            ) : (
              /* HONEST EMPTY STATE — no fabricated level/skill/track; useful next actions; roadmap stays browseable below */
              <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 px-5 py-6">
                <h2 className="text-base font-semibold text-slate-900">{experience.emptyState.title}</h2>
                <p className="mt-2 max-w-2xl text-sm text-slate-600">{experience.emptyState.body}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => navigate('/me/upload')}
                    className="rounded-xl bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
                  >
                    Upload a coaching clip
                  </button>
                  {effectiveLessons.length > 0 ? (
                    <button
                      type="button"
                      onClick={() => navigate(buildStudentLessonPath())}
                      className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100"
                    >
                      View My Lessons
                    </button>
                  ) : null}
                </div>
                <p className="mt-4 text-sm text-slate-500">You can still explore the full skill roadmap below.</p>
              </div>
            )}

            <div className="mt-4 rounded-2xl border border-slate-200 bg-white px-4 py-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">How to use this tree</p>
              <p className="mt-2 text-sm text-slate-700">
                The roadmap is the skill framework you can explore — tap any skill to see what it builds, what comes before it, and how a coach may work on it. Browsing a skill is not a claim you have done it.
              </p>
              {effectiveLessons.length > 0 && experience.hasEvidence ? (
                <button
                  type="button"
                  onClick={() => navigate(buildStudentLessonPath())}
                  className="mt-3 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  View My Lessons
                </button>
              ) : null}
            </div>

            <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <h2 className="text-sm font-semibold text-slate-900">Unlocked Branches</h2>
              {unlockedBranches.length === 0 ? (
                <p className="mt-2 text-sm text-slate-500">
                  Branch tracks unlock from intermediate once your lessons demonstrate that level in this sport.
                </p>
              ) : (
                <div className="mt-3 flex flex-wrap gap-2">
                  {unlockedBranches.map((track) => (
                    <span
                      key={track.trackId}
                      className="rounded-full bg-white px-3 py-1 text-sm text-slate-700"
                    >
                      {track.name}
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-6 space-y-6">
              {roadmap.coreTrack && (
                <section className="rounded-2xl border border-slate-200 bg-white p-5">
                  <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Core Progression</p>
                      <h2 className="mt-2 text-lg font-semibold text-slate-950">{roadmap.coreTrack.name}</h2>
                      <p className="text-sm text-slate-500">
                        Your default path from beginner foundations into stronger general performance.
                      </p>
                    </div>
                    <span className="rounded-full bg-indigo-100 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-indigo-700">
                      Visible Tree
                    </span>
                  </div>

                  <div className="mt-6 space-y-4">
                    {roadmap.coreTrack.skills.map((skill, index) => {
                      const isLast = index === roadmap.coreTrack.skills.length - 1;
                      return (
                        <div key={skill.skillId} className="relative pl-10">
                          {!isLast && <div className="absolute left-[15px] top-8 h-[calc(100%+8px)] w-px bg-slate-200" />}
                          <div
                            className={`absolute left-0 top-1 h-8 w-8 rounded-full border-4 ${getNodeClasses(skill.status)}`}
                          />
                          <button
                            ref={(node) => {
                              skillNodeRefs.current[skill.skillId] = node;
                            }}
                            type="button"
                            onClick={() => setSelectedSkillId(skill.skillId)}
                            className={`w-full rounded-2xl border px-4 py-4 text-left ${getRoadmapStatusClasses(skill.status)} ${
                              selectedSkillId === skill.skillId ? 'ring-2 ring-slate-900 ring-offset-2' : 'hover:-translate-y-0.5 hover:shadow-sm'
                            } ${
                              highlightedSkillId === skill.skillId ? 'shadow-lg shadow-indigo-200/60 transition-shadow duration-500' : ''
                            }`}
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <p className="text-xs font-semibold uppercase tracking-wide opacity-70">
                                  {skill.level.replace(/_/g, ' ')}
                                </p>
                                <h3 className="mt-2 text-sm font-semibold">{skill.name}</h3>
                              </div>
                              <span className="rounded-full bg-white/70 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide">
                                {getStatusLabel(skill.status)}
                              </span>
                            </div>
                            <p className="mt-3 text-sm opacity-90">{skill.description}</p>
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </section>
              )}

              <div className="grid gap-6 xl:grid-cols-[minmax(0,1.3fr)_360px]">
                <section className="rounded-2xl border border-slate-200 bg-white p-5">
                  <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Branch Tracks</p>
                      <h2 className="mt-2 text-lg font-semibold text-slate-950">Specialization Paths</h2>
                      <p className="text-sm text-slate-500">
                        These branches unlock later so you can specialize in areas like carving, freeride, freestyle, or all-mountain performance.
                      </p>
                    </div>
                  </div>

                  <div className="mt-5 grid gap-4 xl:grid-cols-2">
                    {roadmap.branchTracks.map((track) => (
                      <section key={track.trackId} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                          <div>
                            <h3 className="text-base font-semibold text-slate-950">{track.name}</h3>
                            <p className="text-sm text-slate-500">
                              {track.isUnlocked
                                ? 'Unlocked branch track'
                                : `Unlocks from ${track.branchFromLevel?.replace(/_/g, ' ') || 'intermediate'}`}
                            </p>
                          </div>
                          <span
                            className={`rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide ${
                              track.isActiveTrack
                                ? 'bg-indigo-100 text-indigo-700'
                                : track.isUnlocked
                                  ? 'bg-emerald-100 text-emerald-700'
                                  : 'bg-slate-100 text-slate-500'
                            }`}
                          >
                            {track.isActiveTrack ? 'Active Track' : track.isUnlocked ? 'Unlocked' : 'Locked'}
                          </span>
                        </div>

                        <div className="mt-4 space-y-3">
                          {track.skills.map((skill) => (
                            <button
                              ref={(node) => {
                                skillNodeRefs.current[skill.skillId] = node;
                              }}
                              key={skill.skillId}
                              type="button"
                              onClick={() => setSelectedSkillId(skill.skillId)}
                              className={`w-full rounded-2xl border px-4 py-4 text-left ${getRoadmapStatusClasses(skill.status)} ${
                                selectedSkillId === skill.skillId ? 'ring-2 ring-slate-900 ring-offset-2' : 'hover:-translate-y-0.5 hover:shadow-sm'
                              } ${
                                highlightedSkillId === skill.skillId ? 'shadow-lg shadow-indigo-200/60 transition-shadow duration-500' : ''
                              }`}
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div>
                                  <p className="text-xs font-semibold uppercase tracking-wide opacity-70">
                                    {skill.level.replace(/_/g, ' ')}
                                  </p>
                                  <h4 className="mt-2 text-sm font-semibold">{skill.name}</h4>
                                </div>
                                <span className="rounded-full bg-white/70 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide">
                                  {getStatusLabel(skill.status)}
                                </span>
                              </div>
                              <p className="mt-3 text-sm opacity-90">{skill.description}</p>
                            </button>
                          ))}
                        </div>
                      </section>
                    ))}
                  </div>
                </section>

                <aside className="rounded-2xl border border-slate-200 bg-slate-50 p-5 xl:sticky xl:top-6">
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Skill Detail</p>
                  {!skillDetail ? (
                    <p className="mt-4 text-sm text-slate-500">
                      Select a skill node to see what the skill is, what comes before it, and the lesson flow attached to it.
                    </p>
                  ) : (
                    <div className="mt-4 space-y-4">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Selected Skill</p>
                        <h2 className="text-lg font-semibold text-slate-950">{skillDetail.name}</h2>
                        <p className="mt-1 text-sm text-slate-500">{skillDetail.trackName}</p>
                        <span className="mt-3 inline-flex rounded-full bg-white px-3 py-1 text-xs font-semibold uppercase tracking-wide text-slate-700">
                          {getStatusLabel(skillDetail.status)}
                        </span>
                      </div>

                      <p className="text-sm text-slate-700">{skillDetail.description}</p>

                      <div>
                        <h3 className="text-sm font-semibold text-slate-900">Prerequisites</h3>
                        {skillDetail.hasPrerequisites ? (
                          <ul className="mt-2 space-y-2 text-sm text-slate-700">
                            {skillDetail.prerequisiteSkills.map((skill) => (
                              <li key={skill.skillId} className="rounded-xl bg-white px-3 py-2">
                                {skill.name}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="mt-2 text-sm text-slate-500">This is an entry-point skill in its track.</p>
                        )}
                      </div>

                      <div>
                        <h3 className="text-sm font-semibold text-slate-900">Next Skills</h3>
                        {skillDetail.hasNextSkills ? (
                          <ul className="mt-2 space-y-2 text-sm text-slate-700">
                            {skillDetail.nextSkills.map((skill) => (
                              <li key={skill.skillId} className="rounded-xl bg-white px-3 py-2">
                                {skill.name}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="mt-2 text-sm text-slate-500">This is currently the end of this visible branch.</p>
                        )}
                      </div>

                      <div>
                        <h3 className="text-sm font-semibold text-slate-900">{skillDetail.lessonFlowTitle}</h3>
                        <p className="mt-1 text-xs text-slate-500">How a coach may work on this — curriculum guidance, not a record of training you have completed.</p>
                        {skillDetail.hasLessonFlow ? (
                          <div className="mt-3 space-y-3">
                            {skillDetail.lessonFlowStages.map((stage) => (
                              <div key={stage.stage} className="rounded-xl bg-white px-3 py-3">
                                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                  {stage.title}
                                </p>
                                {stage.drills.length > 0 ? (
                                  <ul className="mt-2 space-y-1 text-sm text-slate-700">
                                    {stage.drills.map((drill) => (
                                      <li key={drill.drillId}>{drill.name}</li>
                                    ))}
                                  </ul>
                                ) : (
                                  <p className="mt-2 text-sm text-slate-500">No drills yet.</p>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="mt-2 text-sm text-slate-500">{skillDetail.lessonFlowEmptyText}</p>
                        )}
                      </div>
                    </div>
                  )}
                </aside>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default MySkillsPage;
